/**
 * EnvSwitcher — replaces the static env button in the topbar with a real
 * dropdown menu that mutates the environment store.
 *
 * The list of available environments is read from the store (populated by
 * the API once it's wired). For now we ship a static fallback so the
 * dropdown is interactive even before the backend exists.
 */
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { useEnvironment, type Environment, type EnvKind } from "@/stores/environment";

/**
 * Static fallback environments — used when the API hasn't shipped them yet.
 * Once `/api/environments` returns real data, `setAvailable` will replace
 * this list and the dropdown updates automatically.
 */
const FALLBACK: Environment[] = [
  { kind: "prod", org: "guesttek-meraki" },
  { kind: "prod", org: "guesttek-aruba" },
  { kind: "staging", org: "guesttek-staging" },
  { kind: "dev", org: "lab-slc" },
];

const ENV_BADGE: Record<EnvKind, "ok" | "warn" | "info"> = {
  prod: "ok",
  staging: "warn",
  dev: "info",
};

// Stable string id used by RadioGroup. `kind|org` is unique within a list.
const idOf = (e: Environment) => `${e.kind}|${e.org}`;

export function EnvSwitcher() {
  const current = useEnvironment((s) => s.current);
  const available = useEnvironment((s) => s.available);
  const setEnv = useEnvironment((s) => s.set);

  // Fall back when the store doesn't have real envs from the API yet.
  const list = available.length > 1 ? available : FALLBACK;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
        aria-label="Switch environment"
      >
        <Badge variant={ENV_BADGE[current.kind]} size="sm">
          {current.kind.toUpperCase()}
        </Badge>
        {current.org}
        <ChevronDown size={12} className="text-fg-mute" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="min-w-[260px]">
        <DropdownMenuLabel>Environment</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={idOf(current)}
          onValueChange={(v) => {
            const next = list.find((e) => idOf(e) === v);
            if (next) setEnv(next);
          }}
        >
          {list.map((e) => (
            <DropdownMenuRadioItem key={idOf(e)} value={idOf(e)}>
              <Badge variant={ENV_BADGE[e.kind]} size="sm">
                {e.kind.toUpperCase()}
              </Badge>
              <span className="text-fg">{e.org}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
